<template>
    <b-card no-body>
        <!-- Filtreler -->
        <div class="row px-1 pt-1 align-items-end">
            <div class="col-md-3 mb-1">
                <label class="form-label">Analiz Dönemi</label>
                <v-date-picker v-model="date" range placeholder="Son 12 ay"></v-date-picker>
            </div>
            <div class="col-md-2 mb-1">
                <label class="form-label">Sözleşme Tipi</label>
                <v-select-list
                    v-model="agreementType"
                    :options="agreementTypes"
                    label="text"
                    :clearable="false"
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-3 mb-1">
                <label class="form-label">Cari</label>
                <v-select-list
                    v-model="selectedCaris"
                    :options="cariOptions"
                    label="Label"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-4 mb-1">
                <label class="form-label">Sözleşme</label>
                <v-select-list
                    v-model="selectedAgreements"
                    :options="agreementOptions"
                    label="Label"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-3 mb-1">
                <label class="form-label">Başlangıç Bölgesi</label>
                <v-select-list
                    v-model="selectedStartRegions"
                    :options="transferRegionList"
                    label="RegionName"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-3 mb-1">
                <label class="form-label">Bitiş Bölgesi</label>
                <v-select-list
                    v-model="selectedEndRegions"
                    :options="transferRegionList"
                    label="RegionName"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-3 mb-1">
                <label class="form-label">Araç Tipi</label>
                <v-select-list
                    v-model="selectedVehicleTypes"
                    :options="vehicleTypeList"
                    label="Name"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-3 mb-1">
                <label class="form-label">Stok</label>
                <v-select-list
                    v-model="selectedStocks"
                    :options="stokList"
                    label="Name"
                    multiple
                    placeholder="Tümü"></v-select-list>
            </div>
            <div class="col-md-auto mb-1">
                <b-form-checkbox v-model="queryData.OnlyActiveRoutes" switch>Sadece aktif rotalar</b-form-checkbox>
            </div>
            <div class="col-md-auto mb-1">
                <b-form-checkbox v-model="queryData.OnlyManuelChanged" switch>Sadece manuel değişenler</b-form-checkbox>
            </div>
            <div class="col-md-auto mb-1">
                <b-form-checkbox
                    v-model="queryData.ExcludeExpiredAgreements"
                    switch
                    v-b-tooltip.hover
                    title="Açıkken süresi dolmuş sözleşmeler yalnızca listeden değil; emsal medyanı, marj ve KPI hesaplarından da çıkarılır.">
                    Süresi dolmuş sözleşmeleri gizle
                </b-form-checkbox>
            </div>
            <div class="col-auto mb-1 ml-auto">
                <v-executer :loading="loading" :action="getReport">Analiz Et</v-executer>
            </div>
        </div>

        <div v-if="report" class="px-1 pb-1">
            <!-- Dönem bilgisi -->
            <div class="small text-muted mb-1">
                Dönem: <strong>{{ report.PeriodStartStr }} - {{ report.PeriodEndStr }}</strong>
                <span class="mx-50">|</span>
                Mazot: {{ report.PeriodStartFuelPriceStr }} ₺ → {{ report.PeriodEndFuelPriceStr }} ₺
                <span :class="rateClass(report.PeriodFuelChangeRateStr)">({{ report.PeriodFuelChangeRateStr }})</span>
                <b-badge v-if="report.ExcludedExpiredAgreements" variant="light-secondary" class="ml-50">
                    Süresi dolmuş sözleşmeler hesaba dahil değil
                </b-badge>
            </div>

            <div v-if="report.IsTruncated" class="alert alert-warning py-50 px-1 mb-1">
                Filtreye uyan <strong>{{ report.Summary.TotalRowCount }}</strong> satırdan ilk
                <strong>{{ report.Rows.length }}</strong> tanesi listelenmiştir. Excel çıktısı da bu satırları içerir;
                tamamı için filtreleri daraltın.
            </div>

            <!-- KPI şeridi -->
            <div class="row mb-1">
                <div class="col-6 col-md mb-1">
                    <div class="stat-box"><div class="stat-label">Rota</div><div class="stat-val">{{ report.Summary.RouteCount }}</div>
                    <div class="stat-sub">{{ report.Summary.AgreementCount }} sözleşme / {{ report.Summary.CariCount }} cari</div></div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div class="stat-box"><div class="stat-label">Ort. Fiyat Değişimi</div><div class="stat-val">{{ report.Summary.AverageChangeRateStr || '-' }}</div>
                    <div class="stat-sub">Mazot: {{ report.Summary.FuelChangeRateStr || '-' }}</div></div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div :class="['stat-box', reflectionBoxClass]">
                        <div class="stat-label">Ort. Yansıtma</div><div class="stat-val">{{ report.Summary.AverageReflectionRateStr || '-' }}</div>
                        <div class="stat-sub">{{ report.Summary.UnderReflectedRouteCount }} rota %100 altında</div>
                    </div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div :class="['stat-box', report.Summary.AgedRouteCount > 0 ? 'stat-warning' : '']">
                        <div class="stat-label">Yaşlanma</div><div class="stat-val">{{ report.Summary.AgedRouteCount }}</div>
                        <div class="stat-sub">90+ gün zamsız · ort. {{ report.Summary.AverageDaysSinceLastChange }} gün</div>
                    </div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div :class="['stat-box', report.Summary.NegativeMarginRouteCount > 0 ? 'stat-danger' : '']">
                        <div class="stat-label">Ort. Marj</div><div class="stat-val">{{ report.Summary.AverageMarginRateStr || '-' }}</div>
                        <div class="stat-sub">{{ report.Summary.NegativeMarginRouteCount }} negatif / {{ report.Summary.MarginMatchedRouteCount }} eşleşme</div>
                    </div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div class="stat-box"><div class="stat-label">Emsal Farkı</div><div class="stat-val">{{ report.Summary.AveragePriceSpreadRateStr || '-' }}</div>
                    <div class="stat-sub">{{ report.Summary.BenchmarkGroupCount }} rotada çoklu fiyat</div></div>
                </div>
                <div class="col-6 col-md mb-1">
                    <div class="stat-box"><div class="stat-label">Manuel Müdahale</div><div class="stat-val">{{ report.Summary.ManuelChangedRouteCount }}</div>
                    <div class="stat-sub">rota</div></div>
                </div>
            </div>

            <!-- Sekmeler -->
            <ul class="nav nav-tabs mb-1">
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'rows' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('rows')">
                        Rota Analizi ({{ filteredRows.length }})
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'benchmark' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('benchmark')">
                        Emsal Karşılaştırma ({{ (report.Benchmarks || []).length }})
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'scorecard' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('scorecard')">
                        Sağlık Karnesi
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'volume' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('volume')">
                        Hacim Etkisi
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'bridge' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('bridge')">
                        Zam Kaynakları
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'manuel' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('manuel')">
                        Manuel Denetim
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'simulation' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('simulation')">
                        Simülasyon
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'heatmap' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('heatmap')">
                        Koridor Haritası
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'anomaly' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('anomaly')">
                        Anomaliler
                    </a>
                </li>
                <li class="nav-item">
                    <a :class="['nav-link', tab === 'renewal' ? 'active' : '']" href="#" v-on:click.prevent="selectTab('renewal')">
                        Yenileme Takvimi
                    </a>
                </li>
            </ul>

            <!-- Sekmeye özel eylemler: her sekme kendi Excel'ini ve kendi lejantını taşır -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-1">
                <b-button size="sm" variant="flat-secondary" class="py-25" v-on:click="showLegend = !showLegend">
                    <feather-icon icon="InfoIcon" size="14" class="mr-25"></feather-icon>
                    {{ showLegend ? 'Lejantı Gizle' : 'Hesaplama Lejantı' }}
                </b-button>
                <b-button size="sm" variant="success" :disabled="exporting" v-on:click="exportExcel">
                    <b-spinner v-if="exporting" small class="mr-50"></b-spinner>
                    <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50"></feather-icon>
                    {{ exporting ? 'Hazırlanıyor...' : '“' + activeTabTitle + '” sekmesini Excel’e aktar' }}
                </b-button>
            </div>

            <div v-if="showLegend" class="legend-panel mb-1">
                <div class="legend-title">{{ activeTabTitle }} — Hesaplama Lejantı</div>
                <table class="table table-sm table-bordered compact-report mb-0">
                    <thead>
                        <tr>
                            <th style="min-width:180px">Kutu / Kolon</th>
                            <th>Nasıl hesaplanır</th>
                            <th style="min-width:220px">Dikkat</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(l,i) in activeLegend" :key="'lg'+i">
                            <td class="font-weight-bolder text-wrap" style="white-space:normal">{{ l.Name }}</td>
                            <td class="text-wrap" style="white-space:normal">{{ l.Formula }}</td>
                            <td class="text-wrap text-muted" style="white-space:normal">{{ l.Note || '—' }}</td>
                        </tr>
                        <tr v-if="activeLegend.length === 0">
                            <td colspan="3" class="text-center">Bu sekme için lejant tanımı bulunamadı.</td>
                        </tr>
                    </tbody>
                </table>
                <div class="small text-muted mt-50">
                    Aynı tanımlar Excel çıktısının <strong>Hesaplama Lejantı</strong> sayfasında da yer alır.
                </div>
            </div>

            <!-- Rota analizi -->
            <template v-if="tab === 'rows'">
                <div class="d-flex flex-wrap align-items-center mb-1">
                    <div class="mr-1" style="min-width:240px">
                        <b-form-input v-model="search" size="sm" placeholder="Cari, sözleşme, bölge, stok ara..."></b-form-input>
                    </div>
                    <b-button
                        v-for="preset in presets"
                        :key="preset.key"
                        size="sm"
                        class="mr-50 mb-25 py-25"
                        :variant="activePreset === preset.key ? preset.variant : 'flat-' + preset.variant"
                        v-on:click="togglePreset(preset.key)">
                        {{ preset.text }}
                    </b-button>
                </div>

                <div class="table-responsive">
                    <table class="table table-sm table-bordered compact-report ra-table mb-0">
                        <thead>
                            <tr class="group-row">
                                <th colspan="7" class="ra-sticky ra-group">Rota</th>
                                <th colspan="4" class="ra-group">Fiyat</th>
                                <th colspan="3" class="ra-group">Yansıtma</th>
                                <th colspan="4" class="ra-group">Yaşlanma</th>
                                <th colspan="3" class="ra-group">Emsal</th>
                                <th colspan="4" class="ra-group">Satış-Maliyet Makası</th>
                                <th rowspan="2" class="ra-group"></th>
                            </tr>
                            <tr>
                                <th v-for="col in columns" :key="col.key"
                                    :class="['sortable', col.class, col.sticky ? 'ra-sticky' : '']"
                                    :style="col.style"
                                    v-on:click="sortBy(col.key)">
                                    {{ col.text }}
                                    <span v-if="sort.key === col.key" class="sort-icon">{{ sort.asc ? '▲' : '▼' }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(r,i) in pagedRows" :key="'r'+i" :class="rowClass(r)">
                                <td class="ra-sticky ra-col-0">
                                    {{ r.CariName || r.CariCode || '-' }}
                                    <b-badge v-if="r.IsOutAgreement" variant="light-secondary" class="ml-25">Dış</b-badge>
                                </td>
                                <td class="ra-sticky ra-col-1">
                                    {{ r.AgreementCode || '-' }}
                                    <b-badge
                                        v-if="r.IsExpired"
                                        variant="light-secondary"
                                        v-b-tooltip.hover
                                        :title="'Sözleşme bitiş tarihi: ' + (r.AgreementExpireDateStr || '-')">Süresi Doldu</b-badge>
                                    <b-badge v-else-if="!r.IsActive" variant="light-secondary">Sonlandı</b-badge>
                                </td>
                                <td>{{ r.StartRegion }}</td>
                                <td>{{ r.EndRegion }}</td>
                                <td>{{ r.VehicleType }}</td>
                                <td>{{ r.StockCode }}</td>
                                <td>{{ r.TransportStatus }}</td>

                                <td class="num">{{ r.PeriodStartPriceStr || '-' }}</td>
                                <td class="num font-weight-bolder">{{ r.CurrentPriceStr || '-' }}</td>
                                <td :class="['num', rateClass(r.ChangeRateStr)]">{{ r.ChangeRateStr || '-' }}</td>
                                <td class="num">{{ r.ChangeCount }}</td>

                                <td :class="['num', reflectionClass(r.ReflectionRate)]">{{ r.ReflectionRateStr || '-' }}</td>
                                <td class="num">{{ r.ExpectedPriceStr || '-' }}</td>
                                <td :class="['num', gapClass(r.PriceGap)]">{{ r.PriceGapStr || '-' }}</td>

                                <td class="num text-nowrap">{{ r.LastChangeDateStr || '-' }}</td>
                                <td :class="['num', agingClass(r.DaysSinceLastChange)]">{{ r.DaysSinceLastChange != null ? r.DaysSinceLastChange : '-' }}</td>
                                <td class="num">{{ r.FuelChangeSinceLastChangeStr || '-' }}</td>
                                <td class="num">{{ r.ManuelChangeCount || '' }}</td>

                                <td class="num">{{ r.PeerCount > 1 ? r.PeerCount : '-' }}</td>
                                <td class="num">{{ r.PeerMedianPriceStr || '-' }}</td>
                                <td :class="['num', deviationClass(r.PeerDeviationRate)]">{{ r.PeerDeviationRateStr || '-' }}</td>

                                <td class="num">{{ r.BestCostPriceStr || '-' }}</td>
                                <td class="num">{{ r.MarginAmountStr || '-' }}</td>
                                <td :class="['num', marginClass(r.MarginRate)]">{{ r.MarginRateStr || '-' }}</td>
                                <td :class="['num', r.MarginLagDays < 0 ? 'text-danger' : '']">{{ r.MarginLagDays != null ? r.MarginLagDays : '-' }}</td>

                                <td class="text-center">
                                    <a
                                        class="btn btn-sm btn-flat-primary py-0 px-50"
                                        :href="historyLink(r)"
                                        target="_blank"
                                        title="Sözleşmenin fiyat değişim geçmişini aç">
                                        <feather-icon icon="ExternalLinkIcon" size="14"></feather-icon>
                                    </a>
                                </td>
                            </tr>
                            <tr v-if="filteredRows.length === 0">
                                <td colspan="1000" class="text-center">Filtreye uyan kayıt bulunamadı.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="pageCount > 1" class="d-flex justify-content-between align-items-center mt-1">
                    <small class="text-muted">
                        {{ (page - 1) * pageSize + 1 }} - {{ pageRangeEnd }} / {{ filteredRows.length }} kayıt
                    </small>
                    <b-pagination v-model="page" :total-rows="filteredRows.length" :per-page="pageSize" size="sm" class="mb-0"></b-pagination>
                </div>

                <div class="small text-muted mt-1">
                    <strong>Yansıtma</strong>: dönem içi fiyat değişiminin mazot değişimine oranı; %100 altı, mazot artışının fiyata tam yansımadığını gösterir.
                    <span class="mx-50">|</span>
                    <strong>Beklenen Fiyat</strong>: sözleşmenin yakıt payı formülüne göre oluşması gereken fiyat.
                    <span class="mx-50">|</span>
                    <strong>Gecikme</strong>: satış zammının maliyet zammından kaç gün sonra geldiği; negatif değer makas riskidir.
                    <span class="mx-50">|</span>
                    <span class="row-stale d-inline-block px-25">Soluk satırlar</span>
                    süresi dolmuş sözleşme veya sonlanmış rotadır; kolona göre sıralasanız da listenin sonunda kalırlar.
                </div>
            </template>

            <!-- Emsal karşılaştırma -->
            <template v-else-if="tab === 'benchmark'">
                <div class="mb-1" style="max-width:320px">
                    <b-form-input v-model="benchmarkSearch" size="sm" placeholder="Bölge, araç tipi, stok ara..."></b-form-input>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered compact-report mb-0">
                        <thead>
                            <tr>
                                <th>Başlangıç</th>
                                <th>Bitiş</th>
                                <th>Araç Tipi</th>
                                <th>Stok</th>
                                <th>Taşıma</th>
                                <th class="num">Sözleşme</th>
                                <th class="num">En Düşük</th>
                                <th class="num">Medyan</th>
                                <th class="num">Ortalama</th>
                                <th class="num">En Yüksek</th>
                                <th class="num">Makas</th>
                                <th class="num">En Ucuz Maliyet</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(g,i) in filteredBenchmarks">
                                <tr :key="'g'+i" :class="g.SpreadRate >= 20 ? 'row-alert' : ''">
                                    <td>{{ g.StartRegion }}</td>
                                    <td>{{ g.EndRegion }}</td>
                                    <td>{{ g.VehicleType }}</td>
                                    <td>{{ g.StockCode }}</td>
                                    <td>{{ g.TransportStatus }}</td>
                                    <td class="num">{{ g.AgreementCount }}</td>
                                    <td class="num">{{ g.MinPriceStr }}</td>
                                    <td class="num">{{ g.MedianPriceStr }}</td>
                                    <td class="num">{{ g.AveragePriceStr }}</td>
                                    <td class="num">{{ g.MaxPriceStr }}</td>
                                    <td :class="['num font-weight-bolder', g.SpreadRate >= 20 ? 'text-danger' : '']">{{ g.SpreadRateStr || '-' }}</td>
                                    <td class="num">{{ g.BestCostPriceStr || '-' }}</td>
                                    <td class="text-center">
                                        <button class="btn btn-sm btn-flat-primary py-0 px-50" v-on:click="toggleBenchmark(g.RouteKey)">
                                            <feather-icon :icon="isBenchmarkExpanded(g.RouteKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14"></feather-icon>
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="isBenchmarkExpanded(g.RouteKey)" :key="'g'+i+'-d'">
                                    <td colspan="1000" class="bg-light">
                                        <table class="table table-sm table-bordered compact-report mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Taraf</th>
                                                    <th>Cari</th>
                                                    <th>Sözleşme</th>
                                                    <th class="num">Fiyat</th>
                                                    <th class="num">Medyandan Sapma</th>
                                                    <th class="num">Son Değişim</th>
                                                    <th class="num">Gün</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(it,j) in g.Items" :key="'i'+j" :class="it.IsExpired ? 'row-stale' : ''">
                                                    <td>
                                                        <b-badge :variant="it.IsOutAgreement ? 'light-secondary' : 'light-primary'">
                                                            {{ it.IsOutAgreement ? 'Maliyet' : 'Satış' }}
                                                        </b-badge>
                                                        <b-badge v-if="it.IsExpired" variant="light-secondary" class="ml-25">Süresi Doldu</b-badge>
                                                    </td>
                                                    <td>{{ it.CariName || it.CariCode }}</td>
                                                    <td>{{ [it.AgreementCode, it.AgreementName].filter(Boolean).join(' - ') }}</td>
                                                    <td class="num font-weight-bolder">{{ it.PriceStr }}</td>
                                                    <td :class="['num', deviationClass(parseRate(it.DeviationRateStr))]">{{ it.DeviationRateStr || '-' }}</td>
                                                    <td class="num text-nowrap">{{ it.LastChangeDateStr }}</td>
                                                    <td :class="['num', agingClass(it.DaysSinceLastChange)]">{{ it.DaysSinceLastChange }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </template>
                            <tr v-if="filteredBenchmarks.length === 0">
                                <td colspan="1000" class="text-center">
                                    Aynı rotayı birden fazla sözleşmede fiyatlayan kayıt bulunamadı.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <!-- Katman 2 raporları -->
            <b-overlay v-else-if="isInsightTab" :show="insightsLoading" opacity="0.2">
                <div v-if="!insights && !insightsLoading" class="text-center text-muted py-2">
                    Rapor hazırlanamadı.
                </div>

                <!-- Rapor 5: Sözleşme Sağlık Karnesi -->
                <template v-if="tab === 'scorecard' && insights">
                    <div class="row mb-1">
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box stat-danger"><div class="stat-label">Riskli</div>
                            <div class="stat-val">{{ scoreLevelCount('Riskli') }}</div><div class="stat-sub">skor &lt; 60</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box stat-warning"><div class="stat-label">İzlenmeli</div>
                            <div class="stat-val">{{ scoreLevelCount('İzlenmeli') }}</div><div class="stat-sub">skor 60-79</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box stat-primary"><div class="stat-label">İyi</div>
                            <div class="stat-val">{{ scoreLevelCount('İyi') }}</div><div class="stat-sub">skor 80+</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box"><div class="stat-label">Toplam Sözleşme</div>
                            <div class="stat-val">{{ insights.ScoreCards.length }}</div><div class="stat-sub">karnesi çıkarıldı</div></div>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th class="num">Skor</th>
                                    <th>Durum</th>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th>Taraf</th>
                                    <th class="num">Rota</th>
                                    <th class="num">Ort. Zam</th>
                                    <th class="num">Yansıtma</th>
                                    <th class="num">Ort. Gün</th>
                                    <th class="num">90+ Gün</th>
                                    <th class="num">Manuel</th>
                                    <th class="num">Ort. Marj</th>
                                    <th class="num">Negatif Marj</th>
                                    <th class="num">Bitiş</th>
                                    <th>Bulgular</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(c,i) in insights.ScoreCards" :key="'sc'+i" :class="scoreRowClass(c.ScoreLevel)">
                                    <td class="num font-weight-bolder">{{ c.Score }}</td>
                                    <td><b-badge :variant="scoreVariant(c.ScoreLevel)">{{ c.ScoreLevel }}</b-badge></td>
                                    <td>{{ c.CariName || c.CariCode }}</td>
                                    <td>{{ [c.AgreementCode, c.AgreementName].filter(Boolean).join(' - ') }}</td>
                                    <td>{{ c.IsOutAgreement ? 'Maliyet' : 'Satış' }}</td>
                                    <td class="num">{{ c.ActiveRouteCount }} / {{ c.RouteCount }}</td>
                                    <td :class="['num', rateClass(c.AverageChangeRateStr)]">{{ c.AverageChangeRateStr || '-' }}</td>
                                    <td :class="['num', reflectionClass(c.AverageReflectionRate)]">{{ c.AverageReflectionRateStr || '-' }}</td>
                                    <td :class="['num', agingClass(c.AverageDaysSinceLastChange)]">{{ c.AverageDaysSinceLastChange }}</td>
                                    <td class="num">{{ c.AgedRouteCount || '' }}</td>
                                    <td class="num">{{ c.ManuelRouteCount ? c.ManuelRateStr : '-' }}</td>
                                    <td :class="['num', marginClass(c.AverageMarginRate)]">{{ c.AverageMarginRateStr || '-' }}</td>
                                    <td class="num">{{ c.NegativeMarginRouteCount || '' }}</td>
                                    <td class="num text-nowrap">
                                        {{ c.ExpireDateStr || '-' }}
                                        <span v-if="c.DaysToExpire != null" :class="c.DaysToExpire < 30 ? 'text-danger' : 'text-muted'">
                                            ({{ c.DaysToExpire }}g)
                                        </span>
                                    </td>
                                    <td class="text-wrap" style="white-space:normal;min-width:260px">
                                        <span v-for="(issue,j) in c.Issues" :key="'is'+j" class="d-block">• {{ issue }}</span>
                                        <span v-if="!c.Issues || c.Issues.length === 0" class="text-muted">Bulgu yok</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>

                <!-- Rapor 6: Hacim Ağırlıklı Fiyat Etkisi -->
                <template v-if="tab === 'volume' && insights">
                    <div class="row mb-1">
                        <div class="col-6 col-md mb-1">
                            <div class="stat-box"><div class="stat-label">Eşleşen Rota</div>
                            <div class="stat-val">{{ insights.VolumeImpact.MatchedRouteCount }}</div>
                            <div class="stat-sub">{{ insights.VolumeImpact.UnmatchedRouteCount }} rotada dönem içi taşıma yok</div></div>
                        </div>
                        <div class="col-6 col-md mb-1">
                            <div class="stat-box"><div class="stat-label">Sefer</div>
                            <div class="stat-val">{{ insights.VolumeImpact.TotalSeferCount }}</div>
                            <div class="stat-sub">{{ insights.VolumeImpact.TotalQuantityStr }} ton</div></div>
                        </div>
                        <div class="col-6 col-md mb-1">
                            <div class="stat-box"><div class="stat-label">Basit Ort. Zam</div>
                            <div class="stat-val">{{ insights.VolumeImpact.SimpleAverageChangeRateStr || '-' }}</div>
                            <div class="stat-sub">rota başına eşit ağırlık</div></div>
                        </div>
                        <div class="col-6 col-md mb-1">
                            <div class="stat-box stat-primary"><div class="stat-label">Hacim Ağırlıklı Zam</div>
                            <div class="stat-val">{{ insights.VolumeImpact.WeightedAverageChangeRateStr || '-' }}</div>
                            <div class="stat-sub">ciroya gerçekten yansıyan</div></div>
                        </div>
                        <div class="col-6 col-md mb-1">
                            <div class="stat-box"><div class="stat-label">Tutar Etkisi</div>
                            <div class="stat-val">{{ insights.VolumeImpact.TotalRevenueImpactStr || '-' }}</div>
                            <div class="stat-sub">₺ (birim fark × miktar)</div></div>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th>Başlangıç</th>
                                    <th>Bitiş</th>
                                    <th>Araç Tipi</th>
                                    <th>Stok</th>
                                    <th class="num">Sefer</th>
                                    <th class="num">Miktar (Ton)</th>
                                    <th class="num">Hacim Payı</th>
                                    <th class="num">Dönem Başı</th>
                                    <th class="num">Güncel</th>
                                    <th class="num">Değişim</th>
                                    <th class="num">Tutar Etkisi (₺)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(v,i) in insights.VolumeImpact.Rows" :key="'v'+i">
                                    <td>{{ v.CariName }}</td>
                                    <td>{{ v.AgreementCode }}</td>
                                    <td>{{ v.StartRegion }}</td>
                                    <td>{{ v.EndRegion }}</td>
                                    <td>{{ v.VehicleType }}</td>
                                    <td>{{ v.StockCode }}</td>
                                    <td class="num">{{ v.SeferCount }}</td>
                                    <td class="num">{{ v.QuantityStr }}</td>
                                    <td class="num">{{ v.VolumeShareStr || '-' }}</td>
                                    <td class="num">{{ v.PeriodStartPriceStr }}</td>
                                    <td class="num">{{ v.CurrentPriceStr }}</td>
                                    <td :class="['num', rateClass(v.ChangeRateStr)]">{{ v.ChangeRateStr || '-' }}</td>
                                    <td class="num font-weight-bolder">{{ v.RevenueImpactStr }}</td>
                                </tr>
                                <tr v-if="insights.VolumeImpact.Rows.length === 0">
                                    <td colspan="1000" class="text-center">Dönem içinde eşleşen sefer kaydı bulunamadı.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="small text-muted mt-1">
                        Sefer kaydı; satış sözleşmelerinde bitiş firmasının, dış anlaşmalarda taşıyıcının carisi üzerinden eşleştirilir.
                        Rota fiyatları TL/ton olduğu için tutar etkisi <em>birim fiyat farkı × dönemde taşınan miktar</em> ile hesaplanır.
                    </div>
                </template>

                <!-- Rapor 7: Zam Kaynak Ayrıştırması -->
                <template v-if="tab === 'bridge' && insights">
                    <div class="row">
                        <div class="col-md-5 mb-1">
                            <h6 class="mb-50">Dönem İçi Artışın Kaynağı</h6>
                            <table class="table table-sm table-bordered compact-report mb-0">
                                <thead>
                                    <tr>
                                        <th>Neden</th>
                                        <th class="num">Değişim</th>
                                        <th class="num">Rota</th>
                                        <th class="num">Toplam (₺/ton)</th>
                                        <th class="num">Pay</th>
                                        <th class="num">Ort. Oran</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(b,i) in insights.PriceBridge.Items" :key="'b'+i">
                                        <td><b-badge :variant="causeVariant(b.Cause)">{{ b.Cause }}</b-badge></td>
                                        <td class="num">{{ b.ChangeCount }}</td>
                                        <td class="num">{{ b.RouteCount }}</td>
                                        <td class="num">{{ b.TotalDeltaStr }}</td>
                                        <td class="num font-weight-bolder">{{ b.ShareStr || '-' }}</td>
                                        <td class="num">{{ b.AverageRateStr || '-' }}</td>
                                    </tr>
                                    <tr v-if="insights.PriceBridge.Items.length === 0">
                                        <td colspan="1000" class="text-center">Dönem içinde fiyat değişimi bulunamadı.</td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="insights.PriceBridge.Items.length > 0">
                                    <tr class="font-weight-bolder bg-light">
                                        <td>TOPLAM</td>
                                        <td class="num">{{ insights.PriceBridge.TotalChangeCount }}</td>
                                        <td></td>
                                        <td class="num">{{ insights.PriceBridge.TotalDeltaStr }}</td>
                                        <td class="num">%100,00</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div class="col-md-7 mb-1">
                            <h6 class="mb-50">Kaynak Dağılımı</h6>
                            <vue-apex-charts
                                v-if="bridgeSeries.length > 0"
                                type="bar"
                                height="260"
                                :options="bridgeChartOptions"
                                :series="bridgeSeries"></vue-apex-charts>
                        </div>
                    </div>

                    <h6 class="mb-50 mt-1">Sözleşme Bazında Ayrıştırma</h6>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th class="num">Mazot</th>
                                    <th class="num">Enflasyon</th>
                                    <th class="num">Manuel</th>
                                    <th class="num">Diğer</th>
                                    <th class="num">Toplam</th>
                                    <th class="num">Mazot Payı</th>
                                    <th class="num">Enflasyon Payı</th>
                                    <th class="num">Manuel Payı</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(a,i) in insights.PriceBridge.ByAgreement" :key="'ba'+i">
                                    <td>{{ a.CariName }}</td>
                                    <td>{{ a.AgreementCode }}</td>
                                    <td class="num">{{ a.FuelDeltaStr }}</td>
                                    <td class="num">{{ a.InflationDeltaStr }}</td>
                                    <td class="num">{{ a.ManuelDeltaStr }}</td>
                                    <td class="num">{{ a.OtherDeltaStr }}</td>
                                    <td class="num font-weight-bolder">{{ a.TotalDeltaStr }}</td>
                                    <td class="num">{{ a.FuelShareStr || '-' }}</td>
                                    <td class="num">{{ a.InflationShareStr || '-' }}</td>
                                    <td class="num">{{ a.ManuelShareStr || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="small text-muted mt-1">
                        Değişim nedeni kayıtlarda alan olarak tutulmadığı için manuel bayrağı, günlük mazot kontrol logu
                        ve fiyat parametresi versiyon tarihinden çıkarılır. Eşleşmeyen değişimler <strong>Belirsiz</strong> altında toplanır.
                    </div>
                </template>

                <!-- Rapor 8: Manuel Müdahale Denetimi -->
                <template v-if="tab === 'manuel' && insights">
                    <div class="row mb-1">
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box"><div class="stat-label">Manuel Değişiklik</div>
                            <div class="stat-val">{{ insights.ManuelAudit.TotalCount }}</div>
                            <div class="stat-sub">{{ insights.ManuelAudit.AffectedRouteCount }} rota</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box"><div class="stat-label">Etkilenen Sözleşme</div>
                            <div class="stat-val">{{ insights.ManuelAudit.AffectedAgreementCount }}</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div :class="['stat-box', insights.ManuelAudit.DecreaseCount > 0 ? 'stat-warning' : '']">
                                <div class="stat-label">Aşağı Çekilen</div>
                                <div class="stat-val">{{ insights.ManuelAudit.DecreaseCount }}</div>
                                <div class="stat-sub">otomatik fiyatın altına</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box"><div class="stat-label">Toplam Fark</div>
                            <div class="stat-val">{{ insights.ManuelAudit.TotalDeltaStr || '-' }}</div>
                            <div class="stat-sub">₺/ton</div></div>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th>Tarih</th>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th>Taraf</th>
                                    <th>Başlangıç</th>
                                    <th>Bitiş</th>
                                    <th>Araç Tipi</th>
                                    <th>Stok</th>
                                    <th class="num">Otomatik Fiyat</th>
                                    <th class="num">Manuel Fiyat</th>
                                    <th class="num">Fark</th>
                                    <th class="num">Fark %</th>
                                    <th>Kullanıcı</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(m,i) in insights.ManuelAudit.Rows" :key="'m'+i" :class="m.Delta < 0 ? 'row-warn' : ''">
                                    <td class="text-nowrap">{{ m.DateStr }}</td>
                                    <td>{{ m.CariName }}</td>
                                    <td>{{ m.AgreementCode }}</td>
                                    <td>{{ m.IsOutAgreement ? 'Maliyet' : 'Satış' }}</td>
                                    <td>{{ m.StartRegion }}</td>
                                    <td>{{ m.EndRegion }}</td>
                                    <td>{{ m.VehicleType }}</td>
                                    <td>{{ m.StockCode }}</td>
                                    <td class="num">{{ m.AutomaticPriceStr || '-' }}</td>
                                    <td class="num font-weight-bolder">{{ m.ManuelPriceStr }}</td>
                                    <td :class="['num', gapClass(m.Delta)]">{{ m.DeltaStr || '-' }}</td>
                                    <td :class="['num', rateClass(m.DeltaRateStr)]">{{ m.DeltaRateStr || '-' }}</td>
                                    <td>{{ m.UserName || '-' }}</td>
                                </tr>
                                <tr v-if="insights.ManuelAudit.Rows.length === 0">
                                    <td colspan="1000" class="text-center">Dönem içinde manuel fiyat değişikliği bulunamadı.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </b-overlay>

            <!-- Katman 3 raporları -->
            <b-overlay v-else :show="advancedLoading" opacity="0.2">
                <div v-if="!advanced && !advancedLoading && tab !== 'simulation'" class="text-center text-muted py-2">
                    Rapor hazırlanamadı.
                </div>
                <!-- Rapor 9 + 10: Simülasyon -->
                <template v-if="tab === 'simulation'">
                    <div class="row align-items-end mb-1">
                        <div class="col-md-3 mb-1">
                            <label class="form-label">Alternatif Mazot Eşiği (%)</label>
                            <b-form-input v-model="simulation.SimulationThresholdRate" type="number" step="0.1" size="sm" placeholder="Örn. 3"></b-form-input>
                            <small class="text-muted">Mevcut eşik yerine bu oran uygulansaydı ne olurdu?</small>
                        </div>
                        <div class="col-md-3 mb-1">
                            <label class="form-label">Senaryo Mazot Fiyatı (₺)</label>
                            <b-form-input v-model="simulation.WhatIfFuelPrice" type="number" step="0.01" size="sm" placeholder="Örn. 52,50"></b-form-input>
                        </div>
                        <div class="col-md-3 mb-1">
                            <label class="form-label">veya Mazot Değişimi (%)</label>
                            <b-form-input v-model="simulation.WhatIfFuelChangeRate" type="number" step="0.1" size="sm" placeholder="Örn. 10"></b-form-input>
                        </div>
                        <div class="col-auto mb-1">
                            <v-executer :loading="advancedLoading" :action="loadAdvanced">Simülasyonu Çalıştır</v-executer>
                        </div>
                    </div>

                    <template v-if="advanced">
                        <!-- Eşik simülasyonu -->
                        <div v-if="advanced.ThresholdSimulation.IsRequested">
                            <h6 class="mb-50">
                                Mazot Eşiği Simülasyonu — eşik {{ advanced.ThresholdSimulation.SimulatedThresholdRateStr }} olsaydı
                            </h6>
                            <div class="row mb-1">
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box"><div class="stat-label">Mevcut Eşikle Zam</div>
                                    <div class="stat-val">{{ advanced.ThresholdSimulation.CurrentTriggerCount }}</div>
                                    <div class="stat-sub">{{ advanced.ThresholdSimulation.AgreementCount }} sözleşme</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box stat-primary"><div class="stat-label">Yeni Eşikle Zam</div>
                                    <div class="stat-val">{{ advanced.ThresholdSimulation.SimulatedTriggerCount }}</div>
                                    <div class="stat-sub">tetiklenme sayısı</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box"><div class="stat-label">Birim Fiyat Etkisi</div>
                                    <div class="stat-val">{{ advanced.ThresholdSimulation.TotalUnitPriceImpactStr || '-' }}</div>
                                    <div class="stat-sub">₺/ton toplam</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box stat-primary"><div class="stat-label">Tutar Etkisi</div>
                                    <div class="stat-val">{{ advanced.ThresholdSimulation.TotalRevenueImpactStr || '-' }}</div>
                                    <div class="stat-sub">₺ (dönem hacmiyle)</div></div>
                                </div>
                            </div>
                            <div class="table-responsive mb-2">
                                <table class="table table-sm table-bordered compact-report mb-0">
                                    <thead>
                                        <tr>
                                            <th>Cari</th>
                                            <th>Sözleşme</th>
                                            <th class="num">Mevcut Eşik</th>
                                            <th class="num">Yakıt Payı</th>
                                            <th class="num">Mevcut Zam</th>
                                            <th class="num">Yeni Zam</th>
                                            <th class="num">Mevcut Kümülatif</th>
                                            <th class="num">Yeni Kümülatif</th>
                                            <th class="num">Rota</th>
                                            <th class="num">Birim Etki</th>
                                            <th class="num">Tutar Etkisi (₺)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(t,i) in advanced.ThresholdSimulation.Rows" :key="'t'+i">
                                            <td>{{ t.CariName }}</td>
                                            <td>{{ t.AgreementCode }}</td>
                                            <td class="num">{{ t.CurrentThresholdRateStr }}</td>
                                            <td class="num">{{ t.FuelAffectValueStr }}</td>
                                            <td class="num">{{ t.CurrentTriggerCount }}</td>
                                            <td class="num font-weight-bolder">{{ t.SimulatedTriggerCount }}</td>
                                            <td class="num">{{ t.CurrentCumulativeRateStr }}</td>
                                            <td class="num">{{ t.SimulatedCumulativeRateStr }}</td>
                                            <td class="num">{{ t.RouteCount }}</td>
                                            <td class="num">{{ t.UnitPriceImpactStr }}</td>
                                            <td class="num font-weight-bolder">{{ t.RevenueImpactStr }}</td>
                                        </tr>
                                        <tr v-if="advanced.ThresholdSimulation.Rows.length === 0">
                                            <td colspan="1000" class="text-center">Yakıt etkisi aktif sözleşme bulunamadı.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- What-if senaryosu -->
                        <div v-if="advanced.WhatIf.IsRequested">
                            <h6 class="mb-50">
                                What-If — mazot {{ advanced.WhatIf.CurrentFuelPriceStr }} ₺ yerine
                                {{ advanced.WhatIf.ScenarioFuelPriceStr }} ₺ olursa
                            </h6>
                            <div class="row mb-1">
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box stat-primary"><div class="stat-label">Tetiklenen Sözleşme</div>
                                    <div class="stat-val">{{ advanced.WhatIf.TriggeredAgreementCount }}</div>
                                    <div class="stat-sub">{{ advanced.WhatIf.NotTriggeredAgreementCount }} tetiklenmez</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box"><div class="stat-label">Etkilenen Rota</div>
                                    <div class="stat-val">{{ advanced.WhatIf.TriggeredRouteCount }}</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box"><div class="stat-label">Birim Fiyat Etkisi</div>
                                    <div class="stat-val">{{ advanced.WhatIf.TotalUnitPriceImpactStr || '-' }}</div>
                                    <div class="stat-sub">₺/ton toplam</div></div>
                                </div>
                                <div class="col-6 col-md-3 mb-1">
                                    <div class="stat-box stat-primary"><div class="stat-label">Tutar Etkisi</div>
                                    <div class="stat-val">{{ advanced.WhatIf.TotalRevenueImpactStr || '-' }}</div>
                                    <div class="stat-sub">₺ (dönem hacmiyle)</div></div>
                                </div>
                            </div>
                            <div class="table-responsive">
                                <table class="table table-sm table-bordered compact-report mb-0">
                                    <thead>
                                        <tr>
                                            <th>Durum</th>
                                            <th>Cari</th>
                                            <th>Sözleşme</th>
                                            <th class="num">Taban Mazot</th>
                                            <th class="num">Değişim</th>
                                            <th class="num">Eşik</th>
                                            <th class="num">Uygulanacak Oran</th>
                                            <th class="num">Rota</th>
                                            <th class="num">Birim Etki</th>
                                            <th class="num">Tutar Etkisi (₺)</th>
                                            <th>Sonuç</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(w,i) in advanced.WhatIf.Rows" :key="'w'+i" :class="w.IsTriggered ? '' : 'text-muted'">
                                            <td>
                                                <b-badge :variant="w.IsTriggered ? 'success' : 'secondary'">
                                                    {{ w.IsTriggered ? 'Tetiklenir' : 'Tetiklenmez' }}
                                                </b-badge>
                                            </td>
                                            <td>{{ w.CariName }}</td>
                                            <td>{{ w.AgreementCode }}</td>
                                            <td class="num">{{ w.BaseFuelPriceStr }}</td>
                                            <td :class="['num', rateClass(w.FuelChangeRateStr)]">{{ w.FuelChangeRateStr || '-' }}</td>
                                            <td class="num">{{ w.ThresholdRateStr || '-' }}</td>
                                            <td class="num font-weight-bolder">{{ w.AppliedRateStr || '-' }}</td>
                                            <td class="num">{{ w.RouteCount }}</td>
                                            <td class="num">{{ w.UnitPriceImpactStr || '-' }}</td>
                                            <td class="num font-weight-bolder">{{ w.RevenueImpactStr || '-' }}</td>
                                            <td class="text-wrap" style="white-space:normal;min-width:220px">{{ w.ResultMessage }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div
                            v-if="!advanced.ThresholdSimulation.IsRequested && !advanced.WhatIf.IsRequested"
                            class="text-center text-muted py-2">
                            Yukarıdaki alanlardan en az birini doldurup <strong>Simülasyonu Çalıştır</strong> deyin.
                        </div>
                    </template>
                </template>

                <!-- Rapor 11: Koridor Isı Haritası -->
                <template v-if="tab === 'heatmap' && advanced">
                    <div class="d-flex flex-wrap align-items-center mb-1">
                        <span class="small text-muted mr-1">Renk skalası: dönem içi ortalama fiyat değişimi</span>
                        <span class="heat-legend heat-1 mr-25"></span><span class="small mr-50">düşük</span>
                        <span class="heat-legend heat-3 mr-25"></span><span class="small mr-50">orta</span>
                        <span class="heat-legend heat-5 mr-25"></span><span class="small">yüksek</span>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report ra-table mb-0">
                            <thead>
                                <tr>
                                    <th class="ra-sticky" style="left:0;min-width:140px">Başlangıç \ Bitiş</th>
                                    <th v-for="e in advanced.Heatmap.EndRegions" :key="'he'+e" class="text-center">{{ e }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="s in advanced.Heatmap.StartRegions" :key="'hs'+s">
                                    <td class="ra-sticky font-weight-bolder" style="left:0">{{ s }}</td>
                                    <td
                                        v-for="e in advanced.Heatmap.EndRegions"
                                        :key="'hc'+s+e"
                                        :class="['text-center', heatClass(heatCell(s,e))]"
                                        v-b-tooltip.hover
                                        :title="heatTitle(heatCell(s,e))">
                                        <template v-if="heatCell(s,e)">
                                            <div class="font-weight-bolder">{{ heatCell(s,e).AverageChangeRateStr || '-' }}</div>
                                            <div class="heat-sub">{{ heatCell(s,e).RouteCount }} rota</div>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>

                <!-- Rapor 12: Anomaliler -->
                <template v-if="tab === 'anomaly' && advanced">
                    <div class="d-flex flex-wrap align-items-center mb-1">
                        <b-button
                            size="sm"
                            class="mr-50 mb-25 py-25"
                            :variant="anomalyType === null ? 'primary' : 'flat-primary'"
                            v-on:click="anomalyType = null">
                            Tümü ({{ advanced.Anomalies.TotalCount }})
                        </b-button>
                        <b-button
                            v-for="(t,i) in advanced.Anomalies.Types"
                            :key="'at'+i"
                            size="sm"
                            class="mr-50 mb-25 py-25"
                            :variant="anomalyType === t.Type ? severityVariant(t.Severity) : 'flat-' + severityVariant(t.Severity)"
                            v-on:click="anomalyType = anomalyType === t.Type ? null : t.Type">
                            {{ t.Type }} ({{ t.Count }})
                        </b-button>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th>Önem</th>
                                    <th>Tür</th>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th>Başlangıç</th>
                                    <th>Bitiş</th>
                                    <th>Araç Tipi</th>
                                    <th>Stok</th>
                                    <th class="num">Güncel Fiyat</th>
                                    <th class="num">Tarih</th>
                                    <th>Açıklama</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(a,i) in filteredAnomalies" :key="'an'+i">
                                    <td><b-badge :variant="severityVariant(a.Severity)">{{ a.Severity }}</b-badge></td>
                                    <td>{{ a.Type }}</td>
                                    <td>{{ a.CariName }}</td>
                                    <td>{{ a.AgreementCode }}</td>
                                    <td>{{ a.StartRegion }}</td>
                                    <td>{{ a.EndRegion }}</td>
                                    <td>{{ a.VehicleType }}</td>
                                    <td>{{ a.StockCode }}</td>
                                    <td class="num">{{ a.CurrentPriceStr }}</td>
                                    <td class="num text-nowrap">{{ a.DateStr }}</td>
                                    <td class="text-wrap" style="white-space:normal;min-width:260px">{{ a.Description }}</td>
                                </tr>
                                <tr v-if="filteredAnomalies.length === 0">
                                    <td colspan="1000" class="text-center">Bulgu yok.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>

                <!-- Rapor 13: Yenileme Takvimi -->
                <template v-if="tab === 'renewal' && advanced">
                    <div class="row align-items-end mb-1">
                        <div class="col-md-3 mb-1">
                            <label class="form-label">Pencere (gün)</label>
                            <b-form-input v-model="simulation.RenewalWindowDays" type="number" size="sm"></b-form-input>
                        </div>
                        <div class="col-auto mb-1">
                            <v-executer :loading="advancedLoading" :action="loadAdvanced">Yenile</v-executer>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box stat-danger"><div class="stat-label">Süresi Dolmuş</div>
                            <div class="stat-val">{{ advanced.RenewalCalendar.ExpiredCount }}</div></div>
                        </div>
                        <div class="col-6 col-md-3 mb-1">
                            <div class="stat-box stat-warning"><div class="stat-label">Pencerede</div>
                            <div class="stat-val">{{ advanced.RenewalCalendar.ExpiringCount }}</div>
                            <div class="stat-sub">{{ advanced.RenewalCalendar.WindowDays }} gün içinde</div></div>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered compact-report mb-0">
                            <thead>
                                <tr>
                                    <th>Durum</th>
                                    <th class="num">Kalan Gün</th>
                                    <th>Cari</th>
                                    <th>Sözleşme</th>
                                    <th>Taraf</th>
                                    <th class="num">Başlangıç</th>
                                    <th class="num">Bitiş</th>
                                    <th class="num">Rota</th>
                                    <th class="num">Ort. Zam</th>
                                    <th class="num">Yansıtma</th>
                                    <th class="num">Marj</th>
                                    <th class="num">90+ Gün</th>
                                    <th>Görüşmede Öncelik</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(r,i) in advanced.RenewalCalendar.Rows" :key="'rn'+i" :class="r.DaysToExpire < 0 ? 'row-danger' : 'row-warn'">
                                    <td><b-badge :variant="r.DaysToExpire < 0 ? 'danger' : 'warning'">{{ r.Status }}</b-badge></td>
                                    <td class="num font-weight-bolder">{{ r.DaysToExpire }}</td>
                                    <td>{{ r.CariName }}</td>
                                    <td>{{ [r.AgreementCode, r.AgreementName].filter(Boolean).join(' - ') }}</td>
                                    <td>{{ r.IsOutAgreement ? 'Maliyet' : 'Satış' }}</td>
                                    <td class="num text-nowrap">{{ r.StartDateStr || '-' }}</td>
                                    <td class="num text-nowrap">{{ r.ExpireDateStr }}</td>
                                    <td class="num">{{ r.RouteCount }}</td>
                                    <td :class="['num', rateClass(r.AverageChangeRateStr)]">{{ r.AverageChangeRateStr || '-' }}</td>
                                    <td class="num">{{ r.AverageReflectionRateStr || '-' }}</td>
                                    <td class="num">{{ r.AverageMarginRateStr || '-' }}</td>
                                    <td class="num">{{ r.AgedRouteCount || '' }}</td>
                                    <td class="text-wrap" style="white-space:normal;min-width:280px">
                                        <span v-for="(f,j) in r.Focus" :key="'fo'+j" class="d-block">• {{ f }}</span>
                                        <span v-if="!r.Focus || r.Focus.length === 0" class="text-muted">Öne çıkan sorun yok</span>
                                    </td>
                                </tr>
                                <tr v-if="advanced.RenewalCalendar.Rows.length === 0">
                                    <td colspan="1000" class="text-center">Pencerede süresi dolan sözleşme yok.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </b-overlay>
        </div>

        <div v-else-if="!loading" class="text-center text-muted py-3">
            Filtreleri seçip <strong>Analiz Et</strong> butonuna basın.
        </div>
    </b-card>
</template>

<script>
import { BBadge, BButton, BCard, BFormCheckbox, BFormInput, BOverlay, BPagination, BSpinner, VBTooltip } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { AgreementApi } from '@/api/agreement'
import { MaterialApi } from '@/api/material'
import { TransferRegionApi } from '@/api/transfer-region'
import { VehicleTypeApi } from '@/api/vehicle-type'
import Additional from '@/utilities/additional'

const AGED_DAYS = 90

// Katman 2 ve 3 raporları ayrı uç noktalardan gelir; ilgili sekme ilk açıldığında yüklenir.
const INSIGHT_TABS = ['scorecard', 'volume', 'bridge', 'manuel']
const ADVANCED_TABS = ['simulation', 'heatmap', 'anomaly', 'renewal']

export default {
    components: {
        BBadge,
        BButton,
        BCard,
        BFormCheckbox,
        BFormInput,
        BOverlay,
        BPagination,
        BSpinner,
        VueApexCharts
    },
    directives: {
        'b-tooltip': VBTooltip
    },
    data() {
        return {
            loading: false,
            report: null,
            insights: null,
            insightsLoading: false,
            advanced: null,
            advancedLoading: false,
            exporting: false,
            showLegend: false,
            legends: [],
            anomalyType: null,
            simulation: {
                SimulationThresholdRate: null,
                WhatIfFuelPrice: null,
                WhatIfFuelChangeRate: null,
                RenewalWindowDays: 90
            },
            tab: 'rows',
            search: '',
            benchmarkSearch: '',
            activePreset: null,
            page: 1,
            pageSize: 100,
            sort: { key: null, asc: true },
            expandedBenchmarks: {},
            queryData: {
                StartDate: null,
                EndDate: null,
                IsOutAgreement: null,
                CariCodes: [],
                AgreementIds: [],
                StartRegionIds: [],
                EndRegionIds: [],
                VehicleTypeIds: [],
                StockCodes: [],
                OnlyActiveRoutes: true,
                OnlyManuelChanged: false,
                ExcludeExpiredAgreements: false
            },
            agreementList: [],
            transferRegionList: [],
            vehicleTypeList: [],
            stokList: [],
            selectedCaris: [],
            selectedAgreements: [],
            selectedStartRegions: [],
            selectedEndRegions: [],
            selectedVehicleTypes: [],
            selectedStocks: [],
            agreementType: { value: null, text: 'Tümü' },
            agreementTypes: [
                { value: null, text: 'Tümü' },
                { value: false, text: 'Müşteri Sözleşmeleri' },
                { value: true, text: 'Dış Anlaşmalar' }
            ],
            presets: [
                { key: 'erosion', text: 'Erozyon (yansıtma < %100)', variant: 'warning' },
                { key: 'aged', text: '90+ gün zamsız', variant: 'warning' },
                { key: 'negative-margin', text: 'Negatif marj', variant: 'danger' },
                { key: 'peer-gap', text: 'Emsalden %10+ sapan', variant: 'info' },
                { key: 'manuel', text: 'Manuel müdahale', variant: 'primary' }
            ],
            columns: [
                { key: 'CariName', text: 'Cari', sticky: true, style: 'left:0;min-width:150px' },
                { key: 'AgreementCode', text: 'Sözleşme', sticky: true, style: 'left:150px;min-width:110px' },
                { key: 'StartRegion', text: 'Başlangıç' },
                { key: 'EndRegion', text: 'Bitiş' },
                { key: 'VehicleType', text: 'Araç Tipi' },
                { key: 'StockCode', text: 'Stok' },
                { key: 'TransportStatus', text: 'Taşıma' },
                { key: 'PeriodStartPrice', text: 'Dönem Başı', class: 'num' },
                { key: 'CurrentPrice', text: 'Güncel', class: 'num' },
                { key: 'ChangeRate', text: 'Değişim', class: 'num' },
                { key: 'ChangeCount', text: 'Adet', class: 'num' },
                { key: 'ReflectionRate', text: 'Yansıtma', class: 'num' },
                { key: 'ExpectedPriceStr', text: 'Beklenen', class: 'num' },
                { key: 'PriceGap', text: 'Fark', class: 'num' },
                { key: 'LastChangeDateStr', text: 'Son Değişim', class: 'num' },
                { key: 'DaysSinceLastChange', text: 'Gün', class: 'num' },
                { key: 'FuelChangeSinceLastChangeStr', text: 'Mazot Δ', class: 'num' },
                { key: 'ManuelChangeCount', text: 'Manuel', class: 'num' },
                { key: 'PeerCount', text: 'Emsal', class: 'num' },
                { key: 'PeerMedianPriceStr', text: 'Medyan', class: 'num' },
                { key: 'PeerDeviationRate', text: 'Sapma', class: 'num' },
                { key: 'BestCostPriceStr', text: 'Maliyet', class: 'num' },
                { key: 'MarginAmount', text: 'Marj TL', class: 'num' },
                { key: 'MarginRate', text: 'Marj %', class: 'num' },
                { key: 'MarginLagDays', text: 'Gecikme', class: 'num' }
            ]
        }
    },
    mounted() {
        this.getLegends()
        this.getFilterOptions()
        this.getTransferRegions()
        this.getVehicleTypes()
        this.getStoks()
    },
    methods: {
        // Filtreleri istek modeline aktarır. Analiz ve içgörü uçları aynı modeli kullanır.
        syncQueryData() {
            this.queryData.IsOutAgreement = this.agreementType ? this.agreementType.value : null
            this.queryData.CariCodes = this.selectedCaris.map(c => c.CariCode)
            this.queryData.AgreementIds = this.selectedAgreements.map(a => a.Id)
            this.queryData.StartRegionIds = this.selectedStartRegions.map(r => r.Id)
            this.queryData.EndRegionIds = this.selectedEndRegions.map(r => r.Id)
            this.queryData.VehicleTypeIds = this.selectedVehicleTypes.map(v => v.Id)
            this.queryData.StockCodes = this.selectedStocks.map(s => s.Code)
        },
        getReport() {
            this.loading = true
            this.syncQueryData()

            return AgreementApi.RouteAnalysis(this.queryData).then(res => {
                if (res.data.Success) {
                    this.report = res.data.Data
                    this.page = 1
                    // Filtre değiştiği için Katman 2/3 raporları geçersizleşir; sekmesi
                    // açıldığında yeniden çekilir.
                    this.insights = null
                    this.advanced = null
                    if (INSIGHT_TABS.indexOf(this.tab) > -1) this.loadInsights()
                    if (ADVANCED_TABS.indexOf(this.tab) > -1) this.loadAdvanced()
                } else {
                    this.report = null
                    this.insights = null
                    this.advanced = null
                    this.$result(res)
                }
                this.loading = false
            }).catch(err => {
                this.$result(null, err)
                this.loading = false
            })
        },
        loadInsights() {
            if (this.insightsLoading) return
            this.insightsLoading = true
            this.syncQueryData()
            AgreementApi.RouteInsights(this.queryData).then(res => {
                if (res.data.Success) this.insights = res.data.Data
                else this.$result(res)
                this.insightsLoading = false
            }).catch(err => {
                this.$result(null, err)
                this.insightsLoading = false
            })
        },
        loadAdvanced() {
            this.advancedLoading = true
            this.syncQueryData()
            const data = Object.assign({}, this.queryData, {
                SimulationThresholdRate: this.numberOrNull(this.simulation.SimulationThresholdRate),
                WhatIfFuelPrice: this.numberOrNull(this.simulation.WhatIfFuelPrice),
                WhatIfFuelChangeRate: this.numberOrNull(this.simulation.WhatIfFuelChangeRate),
                RenewalWindowDays: this.numberOrNull(this.simulation.RenewalWindowDays) || 90
            })
            return AgreementApi.RouteAdvancedAnalysis(data).then(res => {
                if (res.data.Success) this.advanced = res.data.Data
                else this.$result(res)
                this.advancedLoading = false
            }).catch(err => {
                this.$result(null, err)
                this.advancedLoading = false
            })
        },
        selectTab(key) {
            this.tab = key
            if (INSIGHT_TABS.indexOf(key) > -1 && !this.insights) this.loadInsights()
            if (ADVANCED_TABS.indexOf(key) > -1 && !this.advanced && !this.advancedLoading) this.loadAdvanced()
        },
        numberOrNull(value) {
            if (value === null || value === undefined || value === '') return null
            const parsed = Number(value)
            return isNaN(parsed) ? null : parsed
        },
        severityVariant(severity) {
            if (severity === 'Yüksek') return 'danger'
            if (severity === 'Orta') return 'warning'
            return 'secondary'
        },
        heatCell(startRegion, endRegion) {
            return this.heatmapIndex[startRegion + '|' + endRegion] || null
        },
        // Koridorun ortalama zam oranı, haritadaki en yüksek orana göre beş kademeye ayrılır.
        heatClass(cell) {
            if (!cell || cell.AverageChangeRate == null) return 'heat-empty'
            const max = this.heatmapMax
            if (max <= 0) return 'heat-1'
            const level = Math.min(5, Math.max(1, Math.ceil(cell.AverageChangeRate / max * 5)))
            return 'heat-' + level
        },
        heatTitle(cell) {
            if (!cell) return ''
            return [
                `${cell.StartRegion} → ${cell.EndRegion}`,
                `${cell.RouteCount} rota / ${cell.AgreementCount} sözleşme`,
                `Ort. fiyat: ${cell.AveragePriceStr || '-'} ₺`,
                `Ort. yansıtma: ${cell.AverageReflectionRateStr || '-'}`,
                `Fiyat makası: ${cell.SpreadRateStr || '-'}`
            ].join(' | ')
        },
        // Lejant tanımları sunucudan gelir; ekran ve Excel aynı metni gösterir.
        getLegends() {
            AgreementApi.RouteAnalysisLegend().then(res => {
                if (res.data.Success) this.legends = res.data.Data || []
            }).catch(() => {})
        },
        getFilterOptions() {
            AgreementApi.RouteAnalysisFilters().then(res => {
                if (res.data.Success) this.agreementList = res.data.Data || []
            }).catch(() => {})
        },
        getTransferRegions() {
            TransferRegionApi.List().then(res => {
                this.transferRegionList = res.data.Data || []
            }).catch(() => {})
        },
        getVehicleTypes() {
            VehicleTypeApi.List().then(res => {
                this.vehicleTypeList = res.data.Data || []
            }).catch(() => {})
        },
        getStoks() {
            MaterialApi.List().then(res => {
                this.stokList = res.data.Data || []
            }).catch(() => {})
        },
        togglePreset(key) {
            this.activePreset = this.activePreset === key ? null : key
            this.page = 1
        },
        sortBy(key) {
            if (this.sort.key === key) this.sort.asc = !this.sort.asc
            else this.sort = { key, asc: true }
            this.page = 1
        },
        toggleBenchmark(routeKey) {
            this.$set(this.expandedBenchmarks, routeKey, !this.expandedBenchmarks[routeKey])
        },
        isBenchmarkExpanded(routeKey) {
            return !!this.expandedBenchmarks[routeKey]
        },
        historyLink(row) {
            const base = row.IsOutAgreement ? '/OutAgreement/PriceChangeHistory/' : '/Agreement/PriceChangeHistory/'
            return base + row.AgreementId
        },
        // Sunucudan gelen "%12,34" biçimindeki metni sayıya çevirir (renklendirme için).
        parseRate(str) {
            if (!str) return null
            const value = Number(String(str).replace('%', '').replace(/\./g, '').replace(',', '.'))
            return isNaN(value) ? null : value
        },
        rateClass(str) {
            if (!str) return null
            return String(str).indexOf('-') > -1 ? 'text-danger' : 'text-success'
        },
        reflectionClass(value) {
            if (value == null) return null
            if (value < 60) return 'text-danger font-weight-bolder'
            if (value < 100) return 'text-warning font-weight-bolder'
            return 'text-success'
        },
        gapClass(value) {
            if (value == null) return null
            return value < 0 ? 'text-danger' : 'text-success'
        },
        agingClass(days) {
            if (days == null) return null
            if (days >= AGED_DAYS * 2) return 'text-danger font-weight-bolder'
            if (days >= AGED_DAYS) return 'text-warning font-weight-bolder'
            return null
        },
        deviationClass(value) {
            if (value == null) return null
            return Math.abs(value) >= 10 ? 'text-warning font-weight-bolder' : null
        },
        marginClass(value) {
            if (value == null) return null
            if (value < 0) return 'text-danger font-weight-bolder'
            if (value < 5) return 'text-warning'
            return 'text-success'
        },
        rowClass(row) {
            // Eski kayıtlar soluk gösterilir; uyarı renkleri güncel satırlara ayrılır.
            if (row.IsStale) return 'row-stale'
            if (row.MarginRate != null && row.MarginRate < 0) return 'row-danger'
            if (row.DaysSinceLastChange != null && row.DaysSinceLastChange >= AGED_DAYS) return 'row-warn'
            return null
        },
        scoreVariant(level) {
            if (level === 'Riskli') return 'danger'
            if (level === 'İzlenmeli') return 'warning'
            return 'success'
        },
        scoreRowClass(level) {
            if (level === 'Riskli') return 'row-danger'
            if (level === 'İzlenmeli') return 'row-warn'
            return null
        },
        scoreLevelCount(level) {
            const cards = this.$safe(this.insights, 'ScoreCards') || []
            return cards.filter(c => c.ScoreLevel === level).length
        },
        causeVariant(cause) {
            switch (cause) {
                case 'Mazot Fiyatı': return 'warning'
                case 'Enflasyon': return 'info'
                case 'Manuel': return 'primary'
                case 'Rota Eklendi': return 'success'
                default: return 'secondary'
            }
        },
        // Açık olan sekmenin verisini xlsx olarak indirir. Dosya adı ve ilk sayfadaki
        // başlık bloğu hangi sekmeden alındığını yazar.
        exportExcel() {
            if (this.exporting) return
            this.exporting = true
            this.syncQueryData()
            const data = Object.assign({}, this.queryData, {
                Section: this.tab,
                SimulationThresholdRate: this.numberOrNull(this.simulation.SimulationThresholdRate),
                WhatIfFuelPrice: this.numberOrNull(this.simulation.WhatIfFuelPrice),
                WhatIfFuelChangeRate: this.numberOrNull(this.simulation.WhatIfFuelChangeRate),
                RenewalWindowDays: this.numberOrNull(this.simulation.RenewalWindowDays) || 90
            })
            AgreementApi.RouteAnalysisExcel(data).then(res => {
                const period = this.report
                    ? `${this.report.PeriodStartStr} - ${this.report.PeriodEndStr}`.replace(/\./g, '-')
                    : ''
                Additional.DownloadFile(res.data, `Rota Fiyat Analizi - ${this.activeTabTitle}${period ? ' - ' + period : ''}.xlsx`)
                this.exporting = false
            }).catch(err => {
                this.$result(null, err)
                this.exporting = false
            })
        }
    },
    computed: {
        date: {
            get() { return [this.queryData.StartDate, this.queryData.EndDate].filter(d => d != null) },
            set(val) {
                this.queryData.StartDate = (val && val[0]) || null
                this.queryData.EndDate = (val && val[1]) || null
            }
        },
        // Sözleşme listesinden türetilen tekil cari listesi
        cariOptions() {
            const map = {}
            this.agreementList.forEach(a => {
                if (a.CariCode && !map[a.CariCode]) {
                    map[a.CariCode] = { CariCode: a.CariCode, Label: [a.CariCode, a.CariName].filter(Boolean).join(' - ') }
                }
            })
            return Object.values(map).sort((a, b) => a.Label.localeCompare(b.Label, 'tr'))
        },
        agreementOptions() {
            const type = this.agreementType ? this.agreementType.value : null
            if (type == null) return this.agreementList
            return this.agreementList.filter(a => a.IsOutAgreement === type)
        },
        reflectionBoxClass() {
            const value = this.parseRate(this.$safe(this.report, 'Summary.AverageReflectionRateStr'))
            if (value == null) return ''
            return value < 100 ? 'stat-warning' : 'stat-primary'
        },
        filteredRows() {
            let rows = (this.report && this.report.Rows) || []

            switch (this.activePreset) {
                case 'erosion':
                    rows = rows.filter(r => r.ReflectionRate != null && r.ReflectionRate < 100)
                    break
                case 'aged':
                    rows = rows.filter(r => r.DaysSinceLastChange != null && r.DaysSinceLastChange >= AGED_DAYS)
                    break
                case 'negative-margin':
                    rows = rows.filter(r => r.MarginRate != null && r.MarginRate < 0)
                    break
                case 'peer-gap':
                    rows = rows.filter(r => r.PeerDeviationRate != null && Math.abs(r.PeerDeviationRate) >= 10)
                    break
                case 'manuel':
                    rows = rows.filter(r => r.ManuelChangeCount > 0)
                    break
            }

            const term = (this.search || '').trim().toLocaleLowerCase('tr')
            if (term) {
                rows = rows.filter(r => [r.CariCode, r.CariName, r.AgreementCode, r.AgreementName,
                    r.StartRegion, r.EndRegion, r.VehicleType, r.StockCode, r.StockName]
                    .filter(v => !!v)
                    .some(v => v.toLocaleLowerCase('tr').indexOf(term) > -1))
            }

            if (this.sort.key) {
                const key = this.sort.key
                const dir = this.sort.asc ? 1 : -1
                rows = rows.slice().sort((a, b) => {
                    // Kolon sıralamasında da süresi dolmuş sözleşmeler sonda kalır.
                    if (!!a.IsStale !== !!b.IsStale) return a.IsStale ? 1 : -1
                    const av = a[key]
                    const bv = b[key]
                    if (av == null && bv == null) return 0
                    if (av == null) return 1
                    if (bv == null) return -1
                    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
                    return String(av).localeCompare(String(bv), 'tr') * dir
                })
            }

            return rows
        },
        pageCount() {
            return Math.ceil(this.filteredRows.length / this.pageSize)
        },
        pageRangeEnd() {
            return Math.min(this.page * this.pageSize, this.filteredRows.length)
        },
        pagedRows() {
            const start = (this.page - 1) * this.pageSize
            return this.filteredRows.slice(start, start + this.pageSize)
        },
        isInsightTab() {
            return INSIGHT_TABS.indexOf(this.tab) > -1
        },
        activeSectionLegend() {
            return this.legends.find(l => l.Section === this.tab) || null
        },
        activeTabTitle() {
            return this.activeSectionLegend ? this.activeSectionLegend.Title : 'Rota Analizi'
        },
        activeLegend() {
            return this.activeSectionLegend ? (this.activeSectionLegend.Items || []) : []
        },
        heatmapIndex() {
            const cells = this.$safe(this.advanced, 'Heatmap.Cells') || []
            const index = {}
            cells.forEach(c => { index[c.StartRegion + '|' + c.EndRegion] = c })
            return index
        },
        heatmapMax() {
            const cells = this.$safe(this.advanced, 'Heatmap.Cells') || []
            return cells.reduce((max, c) => (c.AverageChangeRate != null && c.AverageChangeRate > max ? c.AverageChangeRate : max), 0)
        },
        filteredAnomalies() {
            const rows = this.$safe(this.advanced, 'Anomalies.Rows') || []
            if (!this.anomalyType) return rows
            return rows.filter(r => r.Type === this.anomalyType)
        },
        bridgeSeries() {
            const items = this.$safe(this.insights, 'PriceBridge.Items') || []
            if (items.length === 0) return []
            return [{ name: 'Toplam Artış (₺/ton)', data: items.map(i => i.TotalDelta) }]
        },
        bridgeChartOptions() {
            const items = this.$safe(this.insights, 'PriceBridge.Items') || []
            const shares = items.map(i => i.ShareStr)
            return {
                chart: { toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, distributed: true, barHeight: '60%' } },
                colors: ['#ff9f43', '#00cfe8', '#7367f0', '#28c76f', '#82868b'],
                dataLabels: { enabled: true, formatter: (val, opt) => shares[opt.dataPointIndex] || val },
                legend: { show: false },
                xaxis: { categories: items.map(i => i.Cause) }
            }
        },
        filteredBenchmarks() {
            const groups = (this.report && this.report.Benchmarks) || []
            const term = (this.benchmarkSearch || '').trim().toLocaleLowerCase('tr')
            if (!term) return groups
            return groups.filter(g => [g.StartRegion, g.EndRegion, g.VehicleType, g.StockCode, g.StockName]
                .filter(v => !!v)
                .some(v => v.toLocaleLowerCase('tr').indexOf(term) > -1))
        }
    }
}
</script>

<style scoped>
.compact-report {
    font-size: 0.72rem;
    width: 100%;
}
.compact-report th,
.compact-report td {
    padding: 0.2rem 0.35rem;
    vertical-align: middle;
    white-space: nowrap;
}
.compact-report th.num,
.compact-report td.num {
    text-align: right;
}
.ra-table th.sortable {
    cursor: pointer;
    user-select: none;
}
.ra-table th.ra-group {
    background-color: #f3f2f7;
    text-align: center;
    font-weight: 600;
}
.ra-table th.ra-sticky,
.ra-table td.ra-sticky {
    position: sticky;
    z-index: 2;
    background-color: #fff;
}
.ra-table thead th.ra-sticky {
    z-index: 3;
    background-color: #f3f2f7;
}
.ra-table .group-row th.ra-sticky {
    left: 0;
}
.ra-col-0 {
    left: 0;
    min-width: 150px;
}
.ra-col-1 {
    left: 150px;
    min-width: 110px;
}
.sort-icon {
    font-size: 0.6rem;
    opacity: 0.6;
}
.row-warn td:not(.ra-sticky) {
    background-color: rgba(255, 159, 67, 0.08);
}
.row-danger td:not(.ra-sticky) {
    background-color: rgba(234, 84, 85, 0.1);
}
.row-stale {
    opacity: 0.6;
}
.row-stale td:not(.ra-sticky) {
    background-color: #fafafa;
}
.row-alert {
    background-color: rgba(234, 84, 85, 0.08);
}
.stat-box {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.4rem;
    padding: 0.6rem 0.8rem;
    height: 100%;
}
.stat-box.stat-primary {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.3);
}
.stat-box.stat-warning {
    background: rgba(255, 159, 67, 0.1);
    border-color: rgba(255, 159, 67, 0.35);
}
.stat-box.stat-danger {
    background: rgba(234, 84, 85, 0.1);
    border-color: rgba(234, 84, 85, 0.35);
}
.stat-label {
    font-size: 0.7rem;
    color: #6e6b7b;
}
.stat-val {
    font-size: 1.1rem;
    font-weight: 600;
}
.stat-sub {
    font-size: 0.65rem;
    color: #6e6b7b;
}
.heat-sub {
    font-size: 0.6rem;
    color: #6e6b7b;
}
.heat-empty {
    background-image: repeating-linear-gradient(45deg, #fafafa, #fafafa 4px, #efefef 4px, #efefef 8px);
}
.heat-1 { background-color: rgba(40, 199, 111, 0.18); }
.heat-2 { background-color: rgba(160, 199, 80, 0.22); }
.heat-3 { background-color: rgba(255, 199, 67, 0.28); }
.heat-4 { background-color: rgba(255, 159, 67, 0.34); }
.heat-5 { background-color: rgba(234, 84, 85, 0.38); }
.heat-legend {
    display: inline-block;
    width: 20px;
    height: 12px;
    vertical-align: middle;
    border: 1px solid rgba(0, 0, 0, 0.08);
}
.legend-panel {
    border: 1px solid rgba(115, 103, 240, 0.3);
    background: rgba(115, 103, 240, 0.04);
    border-radius: 0.4rem;
    padding: 0.75rem;
}
.legend-title {
    font-weight: 600;
    margin-bottom: 0.5rem;
}
</style>
